import { initHero } from './hero';
import { fetchBusDepartures } from './bus';
import { fetchTrainDepartures } from './train';
import { fetchWeather } from './weather';
import { fetchNews } from './news';
import { fetchMarketsPrices, initMarketsPeriodSelector, initMarketsToggle } from './oil';
import { fetchElectricityPrices } from './electricity';
import { fetchOnThisDay } from './history';
import { fetchProverb } from './proverb';
import { fetchFestivals } from './festivals';
import { renderEurojackpotCountdown } from './eurojackpot';
import { fetchHockey } from './hockey';
import { fetchAiBubble } from './aibubble';
import { fetchPipelineStatus } from './pipeline';
import { loadWidgetVisibility, applyWidgetOrder, initWidgetToggles, initReorderMode, checkForNewVersion, initUpdateToast, isWidgetVisible } from './settings';
import { isOffline } from './helpers';

const FOCUS_REFRESH_AFTER_MS = 60 * 60 * 1000;
let lastFetchTime = Date.now();
let unfocusedSince: number | null = document.hidden || !document.hasFocus() ? Date.now() : null;
let focusReloadStarted = false;

function scheduleMidnightRefresh(): void {
    const now = new Date();
    const finnishNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Helsinki' }));
    const finnishMidnight = new Date(finnishNow);
    finnishMidnight.setDate(finnishMidnight.getDate() + 1);
    finnishMidnight.setHours(0, 0, 5, 0);
    const msUntilMidnight = finnishMidnight.getTime() - finnishNow.getTime();
    setTimeout(() => location.reload(), msUntilMidnight);
}

/**
 * Polling is pointless — and noisy — while the tab is hidden or the device is
 * offline. Ticks are skipped in those states; the connection-restored and
 * tab-visible handlers below catch the widgets up again.
 */
function poll(fn: () => void, intervalMs: number): void {
    setInterval(() => {
        if (document.hidden || isOffline()) return;
        fn();
    }, intervalMs);
}

function refreshVisibleWidgets(): void {
    lastFetchTime = Date.now();
    if (isWidgetVisible('bus')) fetchBusDepartures();
    if (isWidgetVisible('train')) fetchTrainDepartures();
    if (isWidgetVisible('weather')) fetchWeather();
    if (isWidgetVisible('news')) fetchNews();
    if (isWidgetVisible('markets')) fetchMarketsPrices();
    if (isWidgetVisible('electricity')) fetchElectricityPrices();
    if (isWidgetVisible('aibubble')) fetchAiBubble();
    fetchPipelineStatus();
}

function refreshOnFocusChange(): boolean {
    if (focusReloadStarted) return true;

    if (document.hidden || !document.hasFocus()) {
        unfocusedSince ??= Date.now();
        return false;
    }

    if (unfocusedSince !== null && Date.now() - unfocusedSince > FOCUS_REFRESH_AFTER_MS) {
        if (isOffline()) return false;
        focusReloadStarted = true;
        location.reload();
        return true;
    }

    unfocusedSince = null;
    return false;
}

window.addEventListener('focus', refreshOnFocusChange);
window.addEventListener('blur', refreshOnFocusChange);
window.addEventListener('online', () => {
    if (!refreshOnFocusChange()) refreshVisibleWidgets();
});

document.addEventListener('visibilitychange', () => {
    if (refreshOnFocusChange()) return;
    if (document.visibilityState === 'visible' && !isOffline()) {
        const elapsed = Date.now() - lastFetchTime;
        if (elapsed > 120000) {
            lastFetchTime = Date.now();
            if (isWidgetVisible('bus')) fetchBusDepartures();
            if (isWidgetVisible('train')) fetchTrainDepartures();
            if (isWidgetVisible('weather')) fetchWeather();
        }
    }
});

document.addEventListener('DOMContentLoaded', () => {
    initHero();
    loadWidgetVisibility();
    applyWidgetOrder();
    initWidgetToggles();
    initReorderMode();
    if (isWidgetVisible('proverb')) fetchProverb();
    const proverbBtn = document.getElementById('new-proverb-btn');
    if (proverbBtn) proverbBtn.addEventListener('click', () => fetchProverb());
    if (isWidgetVisible('bus')) fetchBusDepartures();
    if (isWidgetVisible('train')) fetchTrainDepartures();
    if (isWidgetVisible('weather')) fetchWeather();
    if (isWidgetVisible('news')) fetchNews();
    if (isWidgetVisible('markets')) fetchMarketsPrices();
    initMarketsPeriodSelector();
    initMarketsToggle();
    if (isWidgetVisible('electricity')) fetchElectricityPrices();
    if (isWidgetVisible('history')) fetchOnThisDay();
    if (isWidgetVisible('festivals')) fetchFestivals();
    if (isWidgetVisible('hockey')) fetchHockey();
    if (isWidgetVisible('aibubble')) fetchAiBubble();
    if (isWidgetVisible('eurojackpot')) renderEurojackpotCountdown();
    poll(() => { if (isWidgetVisible('bus')) { lastFetchTime = Date.now(); fetchBusDepartures(); } }, 60000);
    poll(() => { if (isWidgetVisible('train')) { lastFetchTime = Date.now(); fetchTrainDepartures(); } }, 60000);
    poll(() => { if (isWidgetVisible('weather')) fetchWeather(); }, 600000);
    poll(() => { if (isWidgetVisible('news')) fetchNews(); }, 600000);
    poll(() => { if (isWidgetVisible('markets')) fetchMarketsPrices(); }, 300000);
    poll(() => { if (isWidgetVisible('electricity')) fetchElectricityPrices(); }, 300000);
    poll(() => { if (isWidgetVisible('aibubble')) fetchAiBubble(); }, 600000);
    initUpdateToast();
    checkForNewVersion();
    poll(checkForNewVersion, 300000);
    fetchPipelineStatus();
    poll(fetchPipelineStatus, 60000);
    scheduleMidnightRefresh();

    // Refresh the pipeline status badge to bypass cache
    const badgeImg = document.querySelector('.dev-panel-badge img') as HTMLImageElement | null;
    if (badgeImg) {
        const baseSrc = badgeImg.getAttribute('data-badge-src') || badgeImg.src;
        const refresh = () => { badgeImg.src = `${baseSrc}&_t=${Date.now()}`; };
        refresh();
        poll(refresh, 300000);
    }
});
